import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { NavTab } from './Sidebar';

interface MobileBottomNavProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
  /** Rôle réel issu de la session (jamais déduit de l'onglet courant). */
  isPro?: boolean;
  /** Un admin n'a ni standard ni pipeline : son espace est la console /admin. */
  isAdmin?: boolean;
  /** Déclenche un appel direct (réservé à un futur flux téléphonie client). */
  onDirectCall?: () => void;
}

/**
 * Barre de navigation mobile — l'espace affiché dépend de la session :
 * tableau de bord + pipeline pour un pro, demandes pour un client,
 * recherche + profil pour tous.
 */
export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  currentTab,
  onSelectTab,
  isPro = false,
  isAdmin = false,
  onDirectCall,
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const navItems: { id: NavTab; label: string; icon: React.ReactNode; isCenter?: boolean }[] =
    isPro
      ? [
          {
            id: 'dashboard',
            label: 'Accueil',
            icon: <GridViewRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'marketplace',
            label: 'Explorer',
            icon: <SearchRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'voice-agent',
            label: 'Standard',
            icon: <PhoneInTalkRoundedIcon sx={{ fontSize: 22 }} />,
            isCenter: true,
          },
          {
            id: 'pipeline',
            label: 'Pipeline',
            icon: <WorkOutlineRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'profile',
            label: 'Profil',
            icon: <PersonOutlineRoundedIcon sx={{ fontSize: 22 }} />,
          },
        ]
      : [
          {
            // Admin : pas d'espace client/pro — la home mène à la console.
            id: isAdmin ? 'admin' : 'dashboard',
            label: isAdmin ? 'Console' : 'Accueil',
            icon: <GridViewRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'marketplace',
            label: 'Explorer',
            icon: <SearchRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'calls',
            label: 'Demandes',
            icon: <WorkOutlineRoundedIcon sx={{ fontSize: 22 }} />,
          },
          {
            id: 'profile',
            label: 'Profil',
            icon: <PersonOutlineRoundedIcon sx={{ fontSize: 22 }} />,
          },
        ];

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1150,
        height: 64,
        display: { xs: 'flex', md: 'none' },
        justifyContent: 'space-around',
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)',
        pb: 'env(safe-area-inset-bottom)',
        bgcolor: isLight ? 'rgba(255, 255, 255, 0.96)' : 'rgba(18, 18, 22, 0.96)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        WebkitTapHighlightColor: 'transparent',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.05)',
        px: 1,
      }}
    >
      {navItems.map((item) => {
        const isActive = currentTab === item.id;

        if (item.isCenter) {
          return (
            <Box
              key={item.id + item.label}
              onClick={() => {
                if (onDirectCall && !isPro) {
                  onDirectCall();
                } else {
                  onSelectTab(item.id);
                }
              }}
              sx={{
                position: 'relative',
                top: -10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '16px',
                  bgcolor: '#FF3567',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 18px rgba(255, 53, 103, 0.45)',
                  transition: 'transform 0.15s ease',
                  '&:active': { transform: 'scale(0.92)' },
                }}
              >
                {item.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: isActive ? '#FF3567' : 'text.secondary',
                  mt: 0.5,
                  letterSpacing: '-0.01em',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            key={item.id + item.label}
            onClick={() => onSelectTab(item.id)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.5,
              cursor: 'pointer',
              color: isActive ? '#FF3567' : 'text.secondary',
              transition: 'all 0.15s ease',
              '&:active': { opacity: 0.7 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 28,
                borderRadius: '12px',
                bgcolor: isActive
                  ? isLight
                    ? 'rgba(255, 53, 103, 0.12)'
                    : 'rgba(255, 53, 103, 0.18)'
                  : 'transparent',
                transition: 'all 0.15s ease',
                mb: 0.25,
              }}
            >
              {item.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.66rem',
                fontWeight: isActive ? 800 : 500,
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
